import React, {useState, useEffect} from "react";
import {Dropdown} from "antd";
import {useTranslation} from "react-i18next";

import {Tag} from "components/Tag";
import {Info} from "components/Info";
import {IconCloseRound} from "components/icons";
import SelectSearchInput from "components/selects/SelectSearchInput";
import {color} from "constants/color.consts";
import cn from "classnames";

// list = [{id, name}]
// text = {emptySelect, emptyData, emptyFiltered, emptySearchInput, info}
// prefixIcon = { empty, selected }
// suffixIcon = {empty }

export const TagSelect = ({
  list,
  value,
  text,
  uppercase,
  small,
  bold,
  tagColor,
  borderTagColor,
  prefixIcon,
  suffixIcon,
  addTagClass,
  addClass,
  disabled,
  viewMode,
  readOnly,
  canClear,
  onMenuToggle,
  onChange,
}) => {
  const {t} = useTranslation();

  const [filtered, setFiltered] = useState(list);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleOpen = (open) => {
    onMenuToggle?.(open);
    setIsDropdownOpen(open);
  };

  const handleSearch = (filteredList) => setFiltered(filteredList);

  const handleSelect = (value) => {
    onChange(value);
    setIsDropdownOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setFiltered(list);
  };

  const ClearIcon = () => (
    <IconCloseRound
      width="16"
      height="16"
      color={color[tagColor]}
      addClass="mr-0 cpointer hght-16 mb-0"
      onClick={handleClear}
    />
  );

  const ItemTag = ({text, prefixIcon, suffixIcon}) => {
    return (
      <Tag
        text={text}
        color={tagColor}
        borderColor={borderTagColor}
        noBackground
        bold={bold}
        small={small}
        disabled={disabled}
        uppercase={uppercase}
        addClass={addTagClass}
        prefixIcon={prefixIcon}
        suffixIcon={suffixIcon}
      />
    );
  };

  const menuProps = {
    style: {maxHeight: 300, overflowY: "auto", boxShadow: "none"},
    items: filtered?.length
      ? filtered.map((item) => ({key: String(item.id), label: item.name}))
      : [{key: "empty", disabled: true, label: t(text.emptyFiltered ?? "NO_DATA")}],
    onClick: ({key}) => {
      const item = filtered?.find((it) => String(it.id) === key);
      item && handleSelect(item);
    },
  };

  const dropdownRender = (menu) => (
    <div className="ant-dropdown-menu" style={{padding: 0}}>
      <div style={{padding: "4px 8px"}}>
        <SelectSearchInput
          initialList={list}
          listResultCallbackAfterSearch={handleSearch}
          fieldSearchBy="name"
          placeholder={t(text.emptySearchInput)}
          small
        />
      </div>
      {menu}
    </div>
  );

  useEffect(() => {
    list?.length && !filtered?.length && setFiltered(list);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  if (value)
    return (
      <div className={cn("df-row-center", {[addClass]: !!addClass})}>
        <ItemTag
          text={value.name}
          prefixIcon={prefixIcon?.selected}
          suffixIcon={!(readOnly || viewMode || disabled) && !!canClear && <ClearIcon />}
        />
      </div>
    );

  if (disabled ? !list : viewMode) return null;

  return (
    <div className={cn("df-row-center", {[addClass]: !!addClass})}>
      <Dropdown
        menu={menuProps}
        dropdownRender={dropdownRender}
        trigger={["click"]}
        open={isDropdownOpen}
        onOpenChange={readOnly || disabled ? undefined : handleOpen}
      >
        <div className="cpointer">
          <ItemTag
            text={t(text.emptySelect)}
            prefixIcon={prefixIcon?.empty}
            suffixIcon={suffixIcon?.empty}
          />
        </div>
      </Dropdown>
      {text.info && <Info tooltip={text.info} addClass="mr-1" />}
    </div>
  );
};
